import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addWeeks, format, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Loader2, Timer, Save, ShieldCheck, Undo2, Search, AlertTriangle, CalendarRange,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import useCentrosPermitidos from '@/hooks/useCentrosPermitidos';
import {
  listarHorasCentros, guardarLimiteEstandar, autorizarHorasSemana, quitarAjusteSemana, guardarRestriccionLlegada,
} from './services/horasAutorizadas.services';
import SemanasCentroDialog from './components/SemanasCentroDialog';

const nf = (n, d = 1) => (n == null ? '—' : Number(n).toLocaleString('es-CO', { maximumFractionDigits: d }));
const lunesDe = (date) => startOfWeek(date, { weekStartsOn: 1 });

// Niveles de la restricción de llegada (se enciende gradualmente por centro)
const MODOS_LLEGADA = [
  { value: 'apagado', label: 'Apagado', clase: 'border-input', ayuda: 'Marca cualquier empleado (como siempre).' },
  { value: 'aviso', label: 'Aviso', clase: 'border-amber-400 text-amber-800 dark:text-amber-300', ayuda: 'Deja marcar, pero las llegadas sin turno quedan como "sin planificar".' },
  { value: 'bloqueo', label: 'Bloqueo', clase: 'border-red-400 text-red-700 dark:text-red-300', ayuda: 'Solo marcan los planificados (el supervisor puede hacer excepciones con motivo).' },
];

// Barra de uso: planificadas vs. límite vigente
const BarraUso = ({ planificadas, limite }) => {
  if (limite == null) return <span className="text-xs text-muted-foreground">Sin límite</span>;
  const pct = limite > 0 ? (planificadas / limite) * 100 : planificadas > 0 ? 100 : 0;
  const color = pct > 100 ? 'bg-red-500' : pct >= 90 ? 'bg-amber-500' : 'bg-emerald-500';
  const disponibles = limite - planificadas;
  return (
    <div className="min-w-[140px]">
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className={`text-xs mt-1 ${disponibles < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-muted-foreground'}`}>
        {disponibles < 0 ? `Excede ${nf(-disponibles)} h` : `${nf(disponibles)} h disponibles`} · {nf(pct, 0)} %
      </div>
    </div>
  );
};

const HorasAutorizadasPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { puedeVerCentro } = useCentrosPermitidos();
  const usuario = useMemo(
    () => ({ id: user?._id || user?.id || undefined, nombre: user?.nombre || user?.name || user?.email || undefined }),
    [user],
  );

  const [semana, setSemana] = useState(() => lunesDe(new Date()));
  const weekStart = format(semana, 'yyyy-MM-dd');
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  // Límite estándar en edición por centro: { [id]: '300' }
  const [edicion, setEdicion] = useState({});
  const [guardando, setGuardando] = useState(null);
  const [autorizar, setAutorizar] = useState(null); // fila del centro
  const [formAut, setFormAut] = useState({ horas: '', motivo: '' });
  const [quitar, setQuitar] = useState(null);
  const [verSemanas, setVerSemanas] = useState(null); // fila del centro
  // Minutos antes del turno en edición por centro: { [id]: '60' }
  const [minutosEdicion, setMinutosEdicion] = useState({});

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const data = await listarHorasCentros(weekStart);
      setFilas((data || []).filter((f) => puedeVerCentro(f.id)));
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setCargando(false);
    }
  }, [weekStart, puedeVerCentro, toast]);

  useEffect(() => { cargar(); }, [cargar]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return q ? filas.filter((f) => f.nombre.toLowerCase().includes(q)) : filas;
  }, [filas, busqueda]);

  const kpis = useMemo(() => ({
    conLimite: filas.filter((f) => f.limite != null).length,
    sinLimite: filas.filter((f) => f.limite == null).length,
    excedidos: filas.filter((f) => f.excede).length,
    ajustes: filas.filter((f) => f.fuente === 'semana').length,
    aviso: filas.filter((f) => f.restriccionLlegada?.modo === 'aviso').length,
    bloqueo: filas.filter((f) => f.restriccionLlegada?.modo === 'bloqueo').length,
  }), [filas]);

  const guardarRestriccion = async (fila, cambios) => {
    const actual = fila.restriccionLlegada || { modo: 'apagado', minutosAntes: 60 };
    const nueva = { ...actual, ...cambios };
    setGuardando(fila.id);
    try {
      await guardarRestriccionLlegada(fila.id, { modo: nueva.modo, minutosAntes: Number(nueva.minutosAntes) }, usuario);
      const modo = MODOS_LLEGADA.find((m) => m.value === nueva.modo);
      toast({
        title: 'Restricción de llegada actualizada',
        description: `${fila.nombre}: ${modo?.label}${nueva.modo !== 'apagado' ? ` · desde ${nueva.minutosAntes} min antes del turno` : ''}`,
      });
      setMinutosEdicion((e) => { const n = { ...e }; delete n[fila.id]; return n; });
      await cargar();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setGuardando(null);
    }
  };

  const guardarEstandar = async (fila) => {
    const valor = edicion[fila.id];
    setGuardando(fila.id);
    try {
      await guardarLimiteEstandar(fila.id, valor === '' ? null : Number(valor), usuario);
      toast({ title: 'Límite actualizado', description: `${fila.nombre}: ${valor === '' ? 'sin límite estándar' : `${valor} h por semana`}` });
      setEdicion((e) => { const n = { ...e }; delete n[fila.id]; return n; });
      await cargar();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setGuardando(null);
    }
  };

  const abrirAutorizar = (fila) => {
    const base = Math.max(fila.limite ?? 0, fila.planificadas ?? 0);
    setFormAut({ horas: fila.ajuste ? String(fila.ajuste.horas) : String(Math.ceil(base)), motivo: '' });
    setAutorizar(fila);
  };

  const confirmarAutorizar = async () => {
    const horas = Number(formAut.horas);
    if (!Number.isFinite(horas) || horas < 0) {
      toast({ title: 'Horas inválidas', description: 'Ingresa un número de horas mayor o igual a 0.', variant: 'destructive' });
      return;
    }
    if (!formAut.motivo.trim()) {
      toast({ title: 'Falta el motivo', description: 'Indica por qué se autorizan estas horas.', variant: 'destructive' });
      return;
    }
    setGuardando(autorizar.id);
    try {
      await autorizarHorasSemana({ laundryCenterId: autorizar.id, weekStart, horas, motivo: formAut.motivo.trim(), usuario });
      toast({ title: 'Horas autorizadas', description: `${autorizar.nombre}: ${nf(horas)} h para la semana del ${format(semana, "d 'de' MMMM", { locale: es })}` });
      setAutorizar(null);
      await cargar();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setGuardando(null);
    }
  };

  const confirmarQuitar = async () => {
    const fila = quitar;
    setQuitar(null);
    setGuardando(fila.id);
    try {
      await quitarAjusteSemana(fila.id, weekStart);
      toast({ title: 'Ajuste eliminado', description: `${fila.nombre} vuelve a su límite estándar en esta semana.` });
      await cargar();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setGuardando(null);
    }
  };

  const etiquetaSemana = `${format(semana, "d 'de' MMM", { locale: es })} al ${format(addWeeks(semana, 1).getTime() - 86400000, "d 'de' MMM yyyy", { locale: es })}`;
  const esEstaSemana = weekStart === format(lunesDe(new Date()), 'yyyy-MM-dd');

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
            <div className="flex items-start gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} title="Volver">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <CardTitle className="text-xl flex items-center gap-2"><Timer className="h-5 w-5" /> Horas autorizadas por centro</CardTitle>
                <CardDescription className="mt-1">
                  Horas por semana que cada centro puede programar en la planificación de turnos (sin contar conductores).
                  Para pasar el límite en una semana, autoriza las horas de esa semana.
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => setSemana((s) => addWeeks(s, -1))}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="w-56 text-center text-sm font-semibold text-muted-foreground">Semana del {etiquetaSemana}</span>
              <Button variant="outline" size="icon" onClick={() => setSemana((s) => addWeeks(s, 1))}><ChevronRight className="h-4 w-4" /></Button>
              {!esEstaSemana && <Button variant="outline" size="sm" onClick={() => setSemana(lunesDe(new Date()))}>Esta semana</Button>}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ['Centros con límite', kpis.conLimite, ''],
              ['Sin límite configurado', kpis.sinLimite, kpis.sinLimite ? 'text-amber-600 dark:text-amber-400' : ''],
              ['Con ajuste esta semana', kpis.ajustes, ''],
              ['Exceden el límite', kpis.excedidos, kpis.excedidos ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'],
              ['Restricción de llegada', `${kpis.aviso} aviso · ${kpis.bloqueo} bloqueo`, kpis.bloqueo ? 'text-red-600 dark:text-red-400 !text-lg' : '!text-lg'],
            ].map(([label, value, cls]) => (
              <div key={label} className="rounded-lg border p-3">
                <div className={`text-2xl font-bold ${cls}`}>{value}</div>
                <div className="text-xs text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>

          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-8" placeholder="Buscar centro…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          </div>

          <div className="overflow-auto rounded-lg border">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="p-2 text-left font-medium">Centro</th>
                  <th className="p-2 text-left font-medium">Límite estándar (h/semana)</th>
                  <th className="p-2 text-left font-medium">Límite de esta semana</th>
                  <th className="p-2 text-right font-medium">Planificadas</th>
                  <th className="p-2 text-left font-medium">Uso</th>
                  <th className="p-2 text-left font-medium" title="Solo marcan la llegada los empleados con turno planificado">Restricción de llegada</th>
                  <th className="p-2 text-right font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {cargando && !filas.length && (
                  <tr><td colSpan={7} className="p-6 text-center text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Cargando…</td></tr>
                )}
                {!cargando && !visibles.length && (
                  <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No hay centros para mostrar.</td></tr>
                )}
                {visibles.map((f) => {
                  const editando = edicion[f.id] !== undefined;
                  const valor = editando ? edicion[f.id] : f.limiteEstandar ?? '';
                  const cambio = editando && String(edicion[f.id]) !== String(f.limiteEstandar ?? '');
                  const ocupado = guardando === f.id;
                  return (
                    <tr key={f.id} className={f.excede ? 'bg-red-50/60 dark:bg-red-950/20' : undefined}>
                      <td className="p-2">
                        <div className="font-medium">{f.nombre}</div>
                        <div className="text-xs text-muted-foreground">{f.empleados} empleado(s) planificados</div>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-1.5">
                          <Input
                            type="number"
                            min={0}
                            step={1}
                            className="h-8 w-24"
                            placeholder="Sin límite"
                            value={valor}
                            onChange={(e) => setEdicion((s) => ({ ...s, [f.id]: e.target.value }))}
                          />
                          {cambio && (
                            <Button size="sm" className="h-8" disabled={ocupado} onClick={() => guardarEstandar(f)}>
                              {ocupado ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                        </div>
                        {f.estandarActualizadoPor?.nombre && (
                          <div className="text-[11px] text-muted-foreground mt-0.5">por {f.estandarActualizadoPor.nombre}</div>
                        )}
                      </td>
                      <td className="p-2">
                        {f.limite == null ? (
                          <Badge variant="outline" className="text-amber-700 border-amber-300 dark:text-amber-300">Sin límite configurado</Badge>
                        ) : (
                          <div>
                            <span className="font-semibold">{nf(f.limite)} h</span>{' '}
                            {f.fuente === 'semana'
                              ? <Badge className="ml-1 bg-indigo-600 hover:bg-indigo-600">Ajuste</Badge>
                              : <Badge variant="secondary" className="ml-1">Estándar</Badge>}
                            {f.ajuste && (
                              <div className="text-[11px] text-muted-foreground mt-0.5 max-w-[240px] truncate" title={f.ajuste.motivo}>
                                {f.ajuste.motivo}{f.ajuste.autorizadoPor?.nombre ? ` · ${f.ajuste.autorizadoPor.nombre}` : ''}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-right tabular-nums">
                        <div className="font-semibold">{nf(f.planificadas)} h</div>
                        {f.horasConductores > 0 && <div className="text-[11px] text-muted-foreground">+{nf(f.horasConductores)} h conductores</div>}
                      </td>
                      <td className="p-2"><BarraUso planificadas={f.planificadas} limite={f.limite} /></td>
                      <td className="p-2">
                        {(() => {
                          const r = f.restriccionLlegada || { modo: 'apagado', minutosAntes: 60 };
                          const modo = MODOS_LLEGADA.find((m) => m.value === r.modo) || MODOS_LLEGADA[0];
                          const minutos = minutosEdicion[f.id] ?? String(r.minutosAntes);
                          const cambioMin = minutosEdicion[f.id] !== undefined && String(minutosEdicion[f.id]) !== String(r.minutosAntes);
                          return (
                            <div className="space-y-1 min-w-[170px]">
                              <select
                                className={`h-8 w-full rounded-md border bg-background px-2 text-sm ${modo.clase}`}
                                value={r.modo}
                                disabled={ocupado}
                                title={modo.ayuda}
                                onChange={(e) => guardarRestriccion(f, { modo: e.target.value })}
                              >
                                {MODOS_LLEGADA.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                              </select>
                              {r.modo !== 'apagado' && (
                                <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                                  desde
                                  <Input
                                    type="number"
                                    min={0}
                                    max={720}
                                    step={5}
                                    className="h-6 w-14 px-1 text-xs"
                                    value={minutos}
                                    onChange={(e) => setMinutosEdicion((s) => ({ ...s, [f.id]: e.target.value }))}
                                    onKeyDown={(e) => e.key === 'Enter' && cambioMin && guardarRestriccion(f, { minutosAntes: minutos })}
                                  />
                                  min antes
                                  {cambioMin && (
                                    <Button size="sm" className="h-6 px-1.5" disabled={ocupado} onClick={() => guardarRestriccion(f, { minutosAntes: minutos })}>
                                      <Save className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="p-2">
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" variant="ghost" className="h-8" onClick={() => setVerSemanas(f)} title="Ver las últimas 4 semanas planeadas y ejecutadas">
                            <CalendarRange className="h-3.5 w-3.5 mr-1" /> Ver semanas
                          </Button>
                          <Button size="sm" variant="outline" className="h-8" disabled={ocupado} onClick={() => abrirAutorizar(f)}>
                            <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Autorizar semana
                          </Button>
                          {f.ajuste && (
                            <Button size="sm" variant="ghost" className="h-8 text-red-600 hover:text-red-700" disabled={ocupado} onClick={() => setQuitar(f)} title="Quitar el ajuste de esta semana">
                              <Undo2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            Planificadas = horas de turnos de la semana sin almuerzo y sin conductores. Un centro sin límite configurado no tiene
            restricción en la planificación. El ajuste de una semana reemplaza el límite estándar solo en esa semana.
            Restricción de llegada: <strong>Aviso</strong> deja marcar a todos pero señala las llegadas sin turno planificado;
            <strong> Bloqueo</strong> solo deja marcar a quien tiene turno (desde los minutos indicados antes del inicio hasta el fin
            del turno) y el supervisor puede registrar excepciones con motivo.
          </p>
        </CardContent>
      </Card>

      {/* Autorizar horas de la semana */}
      <Dialog open={!!autorizar} onOpenChange={(v) => !v && setAutorizar(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Autorizar horas · {autorizar?.nombre}</DialogTitle>
            <DialogDescription>Semana del {etiquetaSemana}. Este valor reemplaza el límite estándar solo en esta semana.</DialogDescription>
          </DialogHeader>
          {autorizar && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div className="rounded border p-2"><div className="font-semibold">{nf(autorizar.limiteEstandar)}{autorizar.limiteEstandar != null ? ' h' : ''}</div><div className="text-[11px] text-muted-foreground">Límite estándar</div></div>
                <div className="rounded border p-2"><div className="font-semibold">{nf(autorizar.planificadas)} h</div><div className="text-[11px] text-muted-foreground">Planificadas</div></div>
                <div className="rounded border p-2"><div className="font-semibold">{autorizar.limite == null ? '—' : `${nf(autorizar.limite)} h`}</div><div className="text-[11px] text-muted-foreground">Límite vigente</div></div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="horas-semana">Horas autorizadas para la semana</Label>
                <Input id="horas-semana" type="number" min={0} step={1} value={formAut.horas} onChange={(e) => setFormAut((s) => ({ ...s, horas: e.target.value }))} />
                {autorizar.limiteEstandar != null && formAut.horas !== '' && (
                  <p className="text-xs text-muted-foreground">
                    {Number(formAut.horas) >= autorizar.limiteEstandar
                      ? `+${nf(Number(formAut.horas) - autorizar.limiteEstandar)} h sobre el estándar`
                      : `${nf(autorizar.limiteEstandar - Number(formAut.horas))} h por debajo del estándar`}
                  </p>
                )}
                {formAut.horas !== '' && Number(formAut.horas) < autorizar.planificadas && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Queda por debajo de lo ya planificado: el centro no podrá agregar horas hasta quitar turnos.
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="motivo">Motivo (obligatorio)</Label>
                <Textarea id="motivo" rows={3} placeholder="Ej.: temporada alta, evento del hotel, reemplazo por incapacidad…" value={formAut.motivo} onChange={(e) => setFormAut((s) => ({ ...s, motivo: e.target.value }))} />
              </div>
              {autorizar.ajuste?.historial?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1">Historial de esta semana</div>
                  <ul className="max-h-32 overflow-auto space-y-1 text-xs text-muted-foreground">
                    {[...autorizar.ajuste.historial].reverse().map((h, i) => (
                      <li key={i}>
                        <span className="font-medium text-foreground">{nf(h.horas)} h</span> · {h.motivo}
                        {h.usuario?.nombre ? ` · ${h.usuario.nombre}` : ''} · {new Date(h.fecha).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutorizar(null)}>Cancelar</Button>
            <Button onClick={confirmarAutorizar} disabled={guardando === autorizar?.id}>
              {guardando === autorizar?.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Autorizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Últimas 4 semanas planeadas vs. ejecutadas */}
      <SemanasCentroDialog centro={verSemanas} hasta={weekStart} onClose={() => setVerSemanas(null)} />

      {/* Quitar ajuste */}
      <AlertDialog open={!!quitar} onOpenChange={(v) => !v && setQuitar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar el ajuste de esta semana?</AlertDialogTitle>
            <AlertDialogDescription>
              {quitar?.nombre} volverá a su límite estándar
              {quitar?.limiteEstandar != null ? ` (${nf(quitar.limiteEstandar)} h)` : ' (sin límite)'} en la semana del {etiquetaSemana}.
              Los turnos ya planificados no se modifican.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarQuitar} className="bg-red-600 hover:bg-red-700">Quitar ajuste</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HorasAutorizadasPage;
